import { h } from 'preact';
import { useState, useEffect, useRef } from 'preact/hooks';
import { translations } from './locales';
import { Category } from './categories';

interface BannerProps {
  onConsent: (accepted: boolean, categories: string[]) => void;
  lang: 'es' | 'en';
}

export function Banner({ onConsent, lang }: BannerProps) {
  const [view, setView] = useState<'main' | 'details'>('main');
  const t = translations[lang] || translations.en;
  const bannerRef = useRef<HTMLDivElement>(null);

  const categories: Array<{ id: Category; name: string; mandatory: boolean; desc: string }> = [
    { id: 'essential', name: t.categories.essential.name, mandatory: true, desc: t.categories.essential.desc },
    { id: 'analytics', name: t.categories.analytics.name, mandatory: false, desc: t.categories.analytics.desc },
    { id: 'marketing', name: t.categories.marketing.name, mandatory: false, desc: t.categories.marketing.desc }
  ];

  const [selected, setSelected] = useState<Category[]>(['essential']);

  // Focus the banner on mount for accessibility
  useEffect(() => {
    if (bannerRef.current) {
      bannerRef.current.focus();
    }
  }, []);

  const handleAcceptAll = () => {
    onConsent(true, categories.map(c => c.id));
  };

  const handleRejectAll = () => {
    onConsent(false, ['essential']);
  };

  const handleSave = () => {
    onConsent(true, selected);
  };

  const toggleCategory = (id: Category) => {
    if (id === 'essential') return;
    if (selected.includes(id)) {
      setSelected(selected.filter(c => c !== id));
    } else {
      setSelected([...selected, id]);
    }
  };

  const baseStyle = {
    position: 'fixed' as const,
    bottom: 0,
    left: 0,
    right: 0,
    background: '#ffffff',
    borderTop: '1px solid #e5e7eb',
    padding: '24px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    fontSize: '14px',
    color: '#111827',
    zIndex: 999999,
    boxShadow: '0 -4px 6px -1px rgba(0, 0, 0, 0.1)',
  };

  const btnStyle = {
    padding: '8px 16px',
    borderRadius: '4px',
    border: 'none',
    cursor: 'pointer',
    fontWeight: 500,
  };

  const primaryBtn = { ...btnStyle, background: '#2563eb', color: '#fff' };
  const secondaryBtn = { ...btnStyle, background: 'transparent', color: '#4b5563', border: '1px solid #d1d5db' };
  const textBtn = { ...btnStyle, background: 'transparent', color: '#4b5563', textDecoration: 'underline', padding: '8px' };
  const closeBtn = {
    position: 'absolute' as const,
    top: '12px',
    right: '12px',
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    color: '#9ca3af',
    fontSize: '20px',
    lineHeight: '1',
    padding: '4px 8px',
    borderRadius: '4px',
  };

  const titleId = 'galletas4all-banner-title';

  if (view === 'details') {
    return (
      <div
        ref={bannerRef}
        style={{ ...baseStyle, position: 'relative' as const }}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
      >
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          <h2 id={titleId} style={{ marginTop: 0, fontSize: '18px' }}>{t.preferencesTitle}</h2>
          <div style={{ margin: '16px 0', display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {categories.map(c => (
              <label key={c.id} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', cursor: c.mandatory ? 'not-allowed' : 'pointer' }}>
                <input 
                  type="checkbox" 
                  checked={selected.includes(c.id)} 
                  disabled={c.mandatory}
                  onChange={() => toggleCategory(c.id)}
                  style={{ marginTop: '4px' }}
                />
                <div>
                  <div style={{ fontWeight: 600 }}>{c.name} {c.mandatory && <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: 'normal' }}>({t.alwaysActive})</span>}</div>
                  <div style={{ fontSize: '13px', color: '#4b5563', marginTop: '2px' }}>{c.desc}</div>
                </div>
              </label>
            ))}
          </div>
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '24px' }}>
            <button style={secondaryBtn} onClick={() => setView('main')}>{t.back}</button>
            <button style={primaryBtn} onClick={handleSave}>{t.savePreferences}</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={bannerRef}
      style={{ ...baseStyle, position: 'relative' as const }}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      tabIndex={-1}
    >
      <div style={{ maxWidth: '800px', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <div>
          <h2 id={titleId} style={{ marginTop: 0, marginBottom: '8px', fontSize: '18px' }}>{t.title}</h2>
          <p style={{ margin: 0, color: '#4b5563', lineHeight: '1.5' }}>
            {t.description}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          <button style={textBtn} onClick={() => setView('details')}>{t.customize}</button>
          <button style={secondaryBtn} onClick={handleRejectAll}>{t.rejectAll}</button>
          <button style={primaryBtn} onClick={handleAcceptAll}>{t.acceptAll}</button>
        </div>
      </div>
    </div>
  );
}
